import { faCheckCircle, faTimesCircle } from "@fortawesome/free-solid-svg-icons";
import classNames from "classnames";
import { ChangeEventHandler, forwardRef, HTMLAttributes, MouseEvent, ReactElement } from "react";
import { useTranslation } from "react-i18next";

import { Button, Field, FieldGroup, FieldLabel, Icon, Loader } from "src/components-v2";
import { PropsWithDataQaid } from "src/types/componentProps";
import { AccountNumberName, FieldAccountNumber } from "./AccountNumberField.styled";

export type AccountNumberFieldProps = HTMLAttributes<HTMLDivElement> &
  PropsWithDataQaid<{
    /**
     * Set input field name
     */
    fieldName: string;

    /**
     * bank
     */
    bank?: string;

    /**
     * Set field input
     */
    value?: string;

    /**
     * Account number result after checking
     */
    result?: string;

    /**
     * Field label (on top of the input field)
     */
    label?: string;

    /**
     * Button check
     */
    buttonCheck?: string;

    /**
     * alternatif position check account component
     */
    isAlternatifComponent?: boolean;

    /**
     * Field placeholder
     */
    placeholder?: string;

    /**
     * Set field to be disabled
     * @default {false}
     */
    disabled?: boolean;

    /**
     * Show result box
     * @default {false}
     */
    isShow?: boolean;

    /**
     * Set loading/checking state (when hitting the API)
     * @default {false}
     */
    isChecking?: boolean;

    /**
     * Set account number success state
     * @default {false}
     */
    isSuccess?: boolean;

    /**
     * Set field invalid (not the wrong state of account number)
     * @default {false}
     */
    isInvalid?: boolean;

    /**
     * Field onChange method
     *
     */
    onChange?: ChangeEventHandler<HTMLInputElement>;

    /**
     * Callback when the check button is clicked
     */
    onClick?: (evt?: MouseEvent<HTMLDivElement>) => void;

    /**
     * Set type of button to be outlined
     * @default {false}
     */
    outline?: boolean;

    /**
     * Set feedback text showed in the bottom of the field
     */
    feedbackText?: string;

    /**
     * set error message showed when inquiry failed
     */
    errorMessage?: ReactElement | null;

    /**
     * Set star on feedback label
     * @default {false}
     */
    required?: boolean;
  }>;

export type AccountNumberNameProps = {
  isSuccess: boolean;
  isWrong: boolean;
};

const defaultProps = {
  bank: "",
  value: "",
  result: "",
  label: "",
  buttonCheck: "",
  isAlternatifComponent: false,
  placeholder: "",
  disabled: false,
  isShow: false,
  isChecking: false,
  isSuccess: false,
  isInvalid: false,
  outline: false,
  feedbackText: "",
  required: false,
  onChange: () => {},
  onClick: () => {},
  errorMessage: null as ReactElement | null
};

const AccountNumberField = forwardRef<HTMLDivElement, AccountNumberFieldProps>((props, ref) => {
  const {
    fieldName,
    bank,
    value,
    result,
    label,
    buttonCheck,
    placeholder,
    isAlternatifComponent,
    disabled,
    isShow,
    isChecking,
    isSuccess,
    isInvalid,
    onChange,
    onClick,
    outline,
    feedbackText,
    required,
    "data-qaid": dataQaid,
    errorMessage,
    ...restProps
  } = props;

  const { t } = useTranslation("translation");

  function checkAccountNumberComponent() {
    if (isChecking) {
      return (
        <div className="u-flex" data-qaid={`${dataQaid}-loading`}>
          <Loader size="small" className="u-mr-3" />
          <p className="u-m-0">{t("check-account.loading")}</p>
        </div>
      );
    }
    if (isSuccess) {
      return (
        <>
          {isAlternatifComponent ? (
            <div className="u-flex" data-qaid={`${dataQaid}-success`}>
              <Icon icon={faCheckCircle} className="u-text-jade u-mt-1 u-mr-1" />
              <p className="u-m-0">
                {bank} - {result}
              </p>
            </div>
          ) : (
            <div className="u-flex u-justify-between" data-qaid={`${dataQaid}-success`}>
              <p className="u-m-0">{result}</p>
              <Icon icon={faCheckCircle} className="u-text-jade u-mt-1" />
            </div>
          )}
        </>
      );
    }
    return (
      <>
        {isAlternatifComponent ? (
          <div className="u-flex" data-qaid={`${dataQaid}-not-valid`}>
            <Icon icon={faTimesCircle} className="u-text-ketchup-tomato u-mt-1 u-mr-1" />
            {errorMessage ? errorMessage : <p className="u-m-0">{t("check-account.not-found")}</p>}
          </div>
        ) : (
          <div className="u-flex u-justify-between" data-qaid={`${dataQaid}-not-valid`}>
            {errorMessage ? errorMessage : <p className="u-m-0">{t("check-account.not-valid")}</p>}
            <Icon icon={faTimesCircle} className="u-text-ketchup-tomato u-mt-1" />
          </div>
        )}
      </>
    );
  }

  function handleClickCheck() {
    if (value.length > 0 && !disabled) onClick();
  }

  return (
    <FieldGroup ref={ref} {...restProps}>
      {label && <FieldLabel required={required}>{label}</FieldLabel>}
      <FieldGroup.Append
        as={outline ? null : Button}
        className={classNames(
          isShow && "u-rounded-br-none",
          outline &&
            "u-text-flip-orange u-border-l-2 u-border-flip-orange u-cursor-pointer u-uppercase hover:u-bg-flip-orange hover:u-text-white"
        )}
        data-qaid={`${dataQaid}-button`}
        onClick={handleClickCheck}
        {...(outline && { variant: "secondary", disabled })}
      >
        {buttonCheck || t("check-account.check")}
      </FieldGroup.Append>
      <FieldAccountNumber
        className={classNames(isInvalid && "is-invalid")}
        type="text"
        pattern="[0-9]*"
        inputMode="numeric"
        name={fieldName}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        isInvalid={isInvalid}
        isShow={isShow}
        disabled={disabled}
        data-qaid={`${dataQaid}-input`}
      />
      {isShow && (
        <AccountNumberName isSuccess={isSuccess && !isChecking} isWrong={!isSuccess && !isChecking}>
          {checkAccountNumberComponent()}
        </AccountNumberName>
      )}
      {feedbackText && (
        <Field.Feedback data-qaid={`${dataQaid}-error-text`} className="u-text-left">
          {feedbackText}
        </Field.Feedback>
      )}
    </FieldGroup>
  );
});

AccountNumberField.defaultProps = defaultProps;

export default AccountNumberField;
